/**
 * ProductList.jsx
 * 製品一覧表示専用コンポーネント
 * 基本単価機能（税抜き標準価格表示）
 * PWA対応: ボトムナビゲーションとの重複回避
 */

import React from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatPrice } from './priceValidationUtils';

const ProductList = ({
  products,
  loading,
  planLimits,
  onEdit,
  onDeleteConfirm,
  onAddProduct
}) => {
  
  if (loading && products.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          製品データを読み込んでいます...
        </Typography>
      </Box>
    );
  }

  if (products.length === 0) {
    return (
      <Box sx={{ 
        p: 4, 
        textAlign: 'center',
        // 🚀 PWA対応: 空の状態でもボトムマージンを確保
        pb: { xs: 8, sm: 4 }
      }}>
        <Typography color="text.secondary" gutterBottom>
          登録されている製品がありません
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          製品を登録して価格管理を開始しましょう
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={onAddProduct}
          sx={{ mt: 2 }}
        >
          製品を登録する
        </Button>
      </Box>
    );
  }

  return (
    <>
      {/* 製品数と制限情報 */}
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.default'
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          製品一覧（{products.length}/{planLimits.maxProducts}件）
        </Typography>
      </Box>
      
      <List sx={{ p: 0 }}>
        {products.map((product) => (
          <ListItem 
            key={product.id}
            divider
            sx={{ 
              '&:last-child': { borderBottom: 'none' },
              p: 2,
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            {/* o3の推奨解決策1: disableTypography を使用 */}
            <ListItemText
              disableTypography
              primary={
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {product.name}
                </Typography>
              }
              secondary={
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    単位: {product.unit}
                  </Typography>
                  {/* 標準価格表示（税抜き） */}
                  {product.standardUnitPrice ? (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'success.main', 
                        fontWeight: 500,
                        mt: 0.5
                      }}
                    >
                      標準価格: ¥{formatPrice(product.standardUnitPrice)}（税抜き）
                    </Typography>
                  ) : (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.disabled',
                        fontStyle: 'italic',
                        mt: 0.5
                      }}
                    >
                      標準価格: 未設定
                    </Typography>
                  )}
                </Box>
              }
            />
            <ListItemSecondaryAction>
              <IconButton 
                edge="end" 
                aria-label="edit"
                onClick={() => onEdit(product)}
                sx={{ mr: 1 }}
                size="small"
              >
                <EditIcon />
              </IconButton>
              <IconButton 
                edge="end" 
                aria-label="delete"
                onClick={() => onDeleteConfirm(product)}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
      
      {/* 🚀 PWA対応: ボトムボタンエリアにマージンを追加 */}
      <Box sx={{ 
        p: 2, 
        textAlign: 'center', 
        borderTop: '1px solid', 
        borderColor: 'divider',
        // PWAボトムナビゲーション用の安全マージンを追加
        pb: { xs: 6, sm: 2 } // モバイル6（48px）、デスクトップ2（16px）
      }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={onAddProduct}
        >
          新しい製品を登録
        </Button>
      </Box>
    </>
  );
};

export default ProductList;